import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { IconsService } from "../../../services/icons/icons.service";

export enum NotificationBannerSeverity {
    ERROR = "error",
    WARNING = "warning",
    SUCCESS = "success",
    INFORMATION = "information",
}

export type NotificationBannerAnnouncementRole = "alert" | "status";

export const NOTIFICATION_BANNER_SEVERITY_LABELS: Record<NotificationBannerSeverity, string> = {
    [NotificationBannerSeverity.ERROR]: "Error",
    [NotificationBannerSeverity.WARNING]: "Warning",
    [NotificationBannerSeverity.SUCCESS]: "Success",
    [NotificationBannerSeverity.INFORMATION]: "Information",
};

export const NOTIFICATION_BANNER_COLOR_CLASSES: Record<NotificationBannerSeverity, string> = {
    [NotificationBannerSeverity.ERROR]: "text-error-800 bg-error-100 border-error-800",
    [NotificationBannerSeverity.WARNING]: "text-warning-800 bg-warning-100 border-warning-800",
    [NotificationBannerSeverity.SUCCESS]: "text-success-800 bg-success-100 border-success-800",
    [NotificationBannerSeverity.INFORMATION]: "text-neutral-600 bg-neutral-100 border-neutral-300",
};

@Component({
    selector: "app-notification-banner",
    templateUrl: "./notification-banner.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FontAwesomeModule],
    standalone: true,
    host: {
        class: "block",
    },
})
export class NotificationBannerComponent {
    private readonly icons = inject(IconsService);

    readonly severity = input.required<NotificationBannerSeverity>();
    readonly showIcon = input<boolean>(true);
    readonly announcementRole = input<NotificationBannerAnnouncementRole>();

    readonly colorClasses = computed<string>(() => NOTIFICATION_BANNER_COLOR_CLASSES[this.severity()]);
    readonly severityLabel = computed<string>(() => NOTIFICATION_BANNER_SEVERITY_LABELS[this.severity()]);
    readonly icon = computed<IconDefinition>(() => {
        switch (this.severity()) {
            case NotificationBannerSeverity.SUCCESS:
                return this.icons.checkCircle;
            case NotificationBannerSeverity.WARNING:
                return this.icons.exclamationCircle;
            case NotificationBannerSeverity.ERROR:
                return this.icons.exclamationTriangle;
            case NotificationBannerSeverity.INFORMATION:
                return this.icons.circleInfo;
        }
    });
}
